import React, { useState } from 'react';
import { X, Star, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VideoEditModal = ({ video, token, onClose, onSaved }) => {
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description);
  const [isExclusive, setIsExclusive] = useState(!!video.is_exclusive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/videos/${video.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          is_exclusive: isExclusive,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Save failed (${res.status})`);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-edit-modal-backdrop"
    >
      <div
        className="bg-zinc-900 border border-red-900/40 rounded-xl w-full max-w-lg p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-edit-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
          data-testid="video-edit-close-btn"
        >
          <X className="h-5 w-5" />
        </button>

        <h3 className="text-xl font-black text-white mb-4">Edit Video</h3>

        <form onSubmit={handleSave} className="space-y-4">
          {error && (
            <div className="bg-red-900/30 border border-red-500/50 text-red-300 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-white font-bold mb-2 text-sm">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500"
              required
              data-testid="video-edit-title-input"
            />
          </div>

          <div>
            <label className="block text-white font-bold mb-2 text-sm">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-black/50 border border-red-900/50 text-white px-4 py-3 rounded focus:outline-none focus:border-red-500 h-32"
              required
              data-testid="video-edit-description-input"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isExclusive}
              onChange={(e) => setIsExclusive(e.target.checked)}
              className="h-5 w-5 rounded border-yellow-500/60 bg-black/50 accent-yellow-400"
              data-testid="video-edit-exclusive-toggle"
            />
            <span className="flex items-center gap-2 text-white font-bold text-sm">
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              <span className="text-yellow-400">EXCLUSIVE</span>
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="video-edit-save-btn"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoEditModal;
